// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: which web build each person's apps run, and the minimum build staff can require.
// The desktop app (Electron) and the phone apps (Capacitor) load the same web build as the
// browser, and a window that stays open keeps its JavaScript until it reloads, so a person can
// be days behind. Every client says which build it runs (POST /konvo/client/hello); staff see
// it on Team insights, nudges wait for a build that can show them, and a minimum build makes
// older clients update. See docs/konvo/assistant-engagement.md, "App versions and updates".
//
// Builds look like 2026.1001.121946: year, month and two-digit day, then the UTC time of day as
// a number (CI: `date -u`, month and time without leading zeros). Local builds are 'dev'.

import {z} from 'zod';

/** Where a client runs: browser tab, installed web app, desktop app, or the phone apps. */
export const CLIENT_PLATFORMS = ['web', 'pwa', 'desktop', 'ios', 'android'] as const;
export type ClientPlatform = (typeof CLIENT_PLATFORMS)[number];

/** The build of a local or unversioned client. Never blocked, never "behind". */
export const DEV_BUILD = 'dev';

/** Devices not seen for this long no longer count for someone's version status. */
export const CLIENT_STALE_DAYS = 14;

const RELEASE_BUILD = /^\d{1,6}(\.\d{1,9}){1,5}$/;

/** The numeric segments of a release build, or null for 'dev', empty or anything else. */
export function parseBuild(build: string | null | undefined): Array<number> | null {
	const value = build?.trim();
	if (!value || !RELEASE_BUILD.test(value)) return null;
	return value.split('.').map(Number);
}

export function isReleaseBuild(build: string | null | undefined): build is string {
	return parseBuild(build) != null;
}

export function isDevBuild(build: string | null | undefined): boolean {
	return build?.trim().toLowerCase() === DEV_BUILD;
}

/**
 * Segment by segment, as numbers (a missing segment is 0): negative when `a` is older, 0 when
 * the same, positive when newer. Null when either is not a release build ('dev', unknown).
 */
export function compareBuilds(a: string | null | undefined, b: string | null | undefined): number | null {
	const left = parseBuild(a);
	const right = parseBuild(b);
	if (!left || !right) return null;
	for (let index = 0; index < Math.max(left.length, right.length); index++) {
		const diff = (left[index] ?? 0) - (right[index] ?? 0);
		if (diff !== 0) return diff < 0 ? -1 : 1;
	}
	return 0;
}

/**
 * Whether `build` is at least `minimum`. No minimum (or an unusable one) is always met; a 'dev'
 * build always meets it (it runs the newest code); an unknown build never does.
 */
export function meetsMinimum(build: string | null | undefined, minimum: string | null | undefined): boolean {
	if (!isReleaseBuild(minimum)) return true;
	if (isDevBuild(build)) return true;
	const diff = compareBuilds(build, minimum);
	return diff != null && diff >= 0;
}

/** The client must update: a release build older than a usable minimum. 'dev' never is. */
export function isBelowMinimum(build: string | null | undefined, minimum: string | null | undefined): boolean {
	if (!isReleaseBuild(build) || !isReleaseBuild(minimum)) return false;
	return (compareBuilds(build, minimum) ?? 0) < 0;
}

/** The newest release build of a list (null when there is none). */
export function newestBuild(builds: ReadonlyArray<string | null | undefined>): string | null {
	let best: string | null = null;
	for (const build of builds) {
		if (!isReleaseBuild(build)) continue;
		if (best == null || (compareBuilds(build, best) ?? 0) > 0) best = build.trim();
	}
	return best;
}

/** When a release build was made (UTC), from its own digits; null for 'dev' or odd builds. */
export function buildDate(build: string | null | undefined): Date | null {
	const parts = parseBuild(build);
	if (!parts || parts.length < 2) return null;
	const [year, monthDay, time = 0] = parts as [number, number, number?];
	const month = Math.floor(monthDay / 100);
	const day = monthDay % 100;
	if (year < 2000 || month < 1 || month > 12 || day < 1 || day > 31) return null;
	const hours = Math.floor(time / 10000);
	const minutes = Math.floor(time / 100) % 100;
	const seconds = time % 100;
	if (hours > 23 || minutes > 59 || seconds > 59) return null;
	const date = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
	return date.getUTCDate() === day ? date : null;
}

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

/** A build as clients send it: a release build or 'dev'. */
export const ClientBuildSchema = z
	.string()
	.trim()
	.max(40)
	.refine((value) => isReleaseBuild(value) || isDevBuild(value), {message: 'not a build version'});

/** The installed app's own operating system (the desktop app; phones report ios/android as platform). */
export const APP_OSES = ['mac', 'windows', 'linux'] as const;
export type AppOs = (typeof APP_OSES)[number];

/**
 * The installed app's own version. The desktop app (Electron) is installed once and only changes
 * when its updater (or a download) replaces it, unlike the web build it loads, which every reload
 * renews. Release desktop versions use the same digits as web builds (2026.1022.73621).
 */
const AppVersionSchema = z.string().trim().max(40);

/** POST /konvo/client/hello */
export const ClientHelloSchema = z.object({
	build: ClientBuildSchema,
	platform: z.enum(CLIENT_PLATFORMS),
	/** The desktop app's installed version (absent from browsers and older apps). */
	app_version: AppVersionSchema.nullish(),
	app_os: z.enum(APP_OSES).nullish(),
	/** x64 or arm64, for the right download. */
	app_arch: z.enum(['x64', 'arm64']).nullish(),
});
export type ClientHello = z.infer<typeof ClientHelloSchema>;

/** What every client must follow: GET /konvo/client/policy and the hello response. */
export const ClientPolicySchema = z.object({
	/** Clients on an older release build must update (null: no minimum). */
	min_build: z.string().nullable(),
	/**
	 * Desktop apps older than this are asked to download and install the newest desktop app
	 * (null: nobody is asked). Absent from older servers.
	 */
	min_desktop_version: z.string().nullable().default(null),
});
export type ClientPolicy = z.infer<typeof ClientPolicySchema>;

export const ClientHelloResponseSchema = z.object({policy: ClientPolicySchema});
export type ClientHelloResponse = z.infer<typeof ClientHelloResponseSchema>;

const ReleaseBuildInput = z
	.string()
	.trim()
	.max(40)
	.refine((value) => isReleaseBuild(value), {message: 'not a release build'});

/** PUT /konvo/insights/client-policy (staff). A field left out keeps its stored value. */
export const SetClientPolicySchema = z.object({
	min_build: ReleaseBuildInput.nullable().optional(),
	min_desktop_version: ReleaseBuildInput.nullable().optional(),
});
export type SetClientPolicy = z.infer<typeof SetClientPolicySchema>;

/** GET/PUT /konvo/insights/client-policy (staff): the stored record and what applies now. */
export const ClientPolicyStateSchema = ClientPolicySchema.extend({
	/** What staff saved (min_build is this unless the server switch is set). */
	stored_min_build: z.string().nullable(),
	/** KONVO_MIN_WEB_BUILD on the server; it wins over the stored minimum while set. */
	env_override: z.string().nullable(),
	updated_at: z.string().nullable(),
	updated_by: z.string().nullable(),
	/** The newest desktop app release (null when the release feed cannot be read). */
	desktop_latest_version: z.string().nullable().default(null),
});
export type ClientPolicyState = z.infer<typeof ClientPolicyStateSchema>;

/** One of someone's apps: what it last said it runs. */
export const ClientDeviceSchema = z.object({
	platform: z.enum(CLIENT_PLATFORMS),
	build: z.string(),
	seen_at: z.string(),
	/** The desktop app's installed version, OS and CPU (null for browsers and apps that predate it). */
	app_version: z.string().nullable().default(null),
	app_os: z.enum(APP_OSES).nullable().default(null),
	app_arch: z.enum(['x64', 'arm64']).nullable().default(null),
});
export type ClientDevice = z.infer<typeof ClientDeviceSchema>;

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

/** Devices seen in the last CLIENT_STALE_DAYS. */
export function recentDevices<T extends {seen_at: string}>(devices: ReadonlyArray<T>, now: Date): Array<T> {
	return devices.filter((device) => {
		const seen = Date.parse(device.seen_at);
		return Number.isFinite(seen) && now.getTime() - seen < CLIENT_STALE_DAYS * DAY_MS;
	});
}

/**
 * Whether a nudge card would show properly: some app has said which build it runs (only builds
 * that know nudge cards report, so reporting is the bar) and every app seen in the last two
 * weeks is at least the required minimum, when there is one. Apps on builds from before
 * reporting cannot be seen at all; "has reported" is the best there is.
 */
export function cardsReady(devices: ReadonlyArray<ClientDevice>, minBuild: string | null, now: Date): boolean {
	if (devices.length === 0) return false;
	return recentDevices(devices, now).every((device) => meetsMinimum(device.build, minBuild));
}

export type ClientVersionStatus = 'up_to_date' | 'behind' | 'unknown';

/**
 * Someone's version for Team insights: unknown when no app has reported in the last two weeks,
 * behind when a recent app is older than `latest` (or below the minimum), else up to date.
 * `behind` lists those apps, oldest build first.
 */
export function clientVersionStatus(
	devices: ReadonlyArray<ClientDevice>,
	latest: string | null,
	minBuild: string | null,
	now: Date,
): {status: ClientVersionStatus; behind: Array<ClientDevice>} {
	const recent = recentDevices(devices, now);
	if (recent.length === 0) return {status: 'unknown', behind: []};
	const behind = recent
		.filter(
			(device) =>
				!meetsMinimum(device.build, minBuild) ||
				(isReleaseBuild(latest) && isReleaseBuild(device.build) && (compareBuilds(device.build, latest) ?? 0) < 0),
		)
		.sort((a, b) => compareBuilds(a.build, b.build) ?? 0);
	return {status: behind.length > 0 ? 'behind' : 'up_to_date', behind};
}

// ---------------------------------------------------------------------------
// Desktop app
// ---------------------------------------------------------------------------

export type DesktopVersionStatus = 'up_to_date' | 'behind' | 'unknown' | 'none';

/**
 * Someone's desktop app for Team insights. none: no desktop app seen in the last two weeks;
 * unknown: it runs, but has not said its version yet (its web build predates reporting) or runs a
 * development copy; behind: older than the latest release (or below the minimum); else up to date.
 */
export function desktopVersionStatus(
	devices: ReadonlyArray<ClientDevice>,
	latest: string | null,
	minVersion: string | null,
	now: Date,
): {status: DesktopVersionStatus; device: ClientDevice | null} {
	const device = recentDevices(devices, now).find((entry) => entry.platform === 'desktop') ?? null;
	if (!device) return {status: 'none', device: null};
	if (!isReleaseBuild(device.app_version)) return {status: 'unknown', device};
	return {status: desktopBehind(device.app_version, latest, minVersion) ? 'behind' : 'up_to_date', device};
}

/** A release desktop version older than the latest release or below the minimum. */
export function desktopBehind(
	version: string | null | undefined,
	latest: string | null,
	minVersion: string | null,
): boolean {
	if (!isReleaseBuild(version)) return false;
	if (isBelowMinimum(version, minVersion)) return true;
	return isReleaseBuild(latest) && (compareBuilds(version, latest) ?? 0) < 0;
}
